export type BackendType = 'GAS' | 'LARAVEL';

export interface BackendConfig {
  type: BackendType;
  apiBaseUrl: string;
  gasApiUrl: string;
  isGAS: boolean;
  isLaravel: boolean;
}

const SUPPORTED_BACKENDS: BackendType[] = ['GAS', 'LARAVEL'];

export function resolveBackendType(value?: string | null): BackendType {
  const normalized = (value || '').trim().toUpperCase();
  return (SUPPORTED_BACKENDS as string[]).includes(normalized)
    ? (normalized as BackendType)
    : 'GAS';
}

export function getBackendConfig(): BackendConfig {
  const env = import.meta.env;
  const type = resolveBackendType(env.VITE_BACKEND_TYPE);
  const apiBaseUrl = env.VITE_API_BASE_URL || '';
  const gasApiUrl = env.VITE_GAS_API_URL || apiBaseUrl;

  return {
    type,
    apiBaseUrl,
    gasApiUrl,
    isGAS: type === 'GAS',
    isLaravel: type === 'LARAVEL',
  };
}

export function isGASBackend(): boolean {
  return getBackendConfig().isGAS;
}

export function isLaravelBackend(): boolean {
  return getBackendConfig().isLaravel;
}

/**
 * Resolves the concrete API endpoint for the active backend.
 * GAS uses VITE_GAS_API_URL (falling back to VITE_API_BASE_URL for
 * backward-compatibility); Laravel uses VITE_API_BASE_URL.
 */
export function getApiBaseUrl(): string {
  const env = import.meta.env;
  if (resolveBackendType(env.VITE_BACKEND_TYPE) === 'GAS') {
    return env.VITE_GAS_API_URL || env.VITE_API_BASE_URL || '';
  }
  return env.VITE_API_BASE_URL || '';
}

/**
 * True when an API endpoint is configured. When false, GAS adapters fall
 * back to their in-memory demo data.
 */
export function hasApiEndpoint(): boolean {
  return getApiBaseUrl() !== '';
}

/**
 * Demo mode is EXPLICITLY opt-in (VITE_DEMO_MODE=true) and only active when
 * no API endpoint is configured. This prevents mock/demo auth from ever
 * being served by a misconfigured production build.
 */
export function isDemoMode(): boolean {
  const flag = String(import.meta.env.VITE_DEMO_MODE || '').toLowerCase();
  return !hasApiEndpoint() && flag === 'true';
}
